import { IPOSFile } from "@/interfaces/pos-files/pos-files";
import { dexieDB } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { initiatePosFilesSync } from "@/services/sync-service";

/**
 * Updates existing Dexie file records with booking/pss metadata and marks them
 * as Pending for sync. Then triggers the sync job.
 *
 * The file blob is already in the Dexie `files` table (written by `insertFile()`
 * in db.ts when the user attaches a file). This function adds the
 * bookingUuid/pssUuid so the sync handler knows where to upload the file.
 */
export async function insertFilesIDs(filesInfo: IPOSFile[]) {
  if (!filesInfo.length) return;

  try {
    LogService.info(
      `Updating ${filesInfo.length} Dexie file records with booking/pss metadata`
    );

    for (const fileInfo of filesInfo) {
      const existing = await dexieDB.files.where({ _id: fileInfo._id }).first();

      if (!existing) {
        LogService.info(
          `File ${fileInfo._id} not found in Dexie, skipping`,
          { fileInfo },
          LogLevel.Error
        );
        continue;
      }

      // Update with booking/pss UUID and mark as Pending for sync
      existing.bookingUuid = fileInfo.data.bookingUuid || undefined;
      existing.pssUuid = fileInfo.data.pssUuid || undefined;
      existing.syncStatus = SyncStatus.Pending;
      await dexieDB.files.put(existing);
    }

    LogService.info(
      `Updated ${filesInfo.length} file records, triggering sync`
    );

    // Trigger the sync job
    initiatePosFilesSync();
  } catch (err) {
    LogService.info(
      `Error updating file records with booking/pss metadata`,
      { err },
      LogLevel.Error
    );
  }
}
